import { useEffect, useRef, useState } from 'react';

import apiService from '../../services/api';
import { NubieAiMedia } from '../../types/api';
import Logger from '../../utils/logger';

/**
 * Les octets des photos d'un site, en URL d'objet, indexees par identifiant de photo.
 *
 * POURQUOI UN CROCHET PARTAGE ET NON DEUX FETCHS. Deux endroits ont besoin des memes
 * octets : les vignettes du panneau Photos, et l'apercu de la page -- ou les `<img>`
 * injectees par l'API pointent sa route d'apercu, que la console remplace par ces URL.
 * Chacun les telechargeant pour son compte, chaque photo passerait deux fois sur le
 * reseau et serait retenue deux fois en memoire.
 *
 * POURQUOI DES URL D'OBJET ET NON LE `src` DE L'API. L'original vit dans un bucket
 * PRIVE, sous un prefixe que le proxy ne sert pas : il n'existe aucune URL publique
 * avant publication. L'API sert donc l'apercu -- mais elle est sur une AUTRE ORIGINE que
 * la console, et une balise `<img src>` ne porterait pas le cookie httpOnly. Il faut
 * passer par le client axios, qui envoie les identifiants.
 *
 * LES URL SONT REVOQUEES, au demontage et quand une photo disparait. Elles sont tenues
 * dans un `useRef` et non dans un etat : une fonction de nettoyage capturerait la valeur
 * du montage, donc vide, et les octets de chaque apercu resteraient retenus jusqu'au
 * rechargement de la page.
 */
export function useApercusMedias(
  siteId: string,
  medias: NubieAiMedia[]
): Record<string, string> {
  const [apercus, setApercus] = useState<Record<string, string>>({});
  const urls = useRef<Record<string, string>>({});

  useEffect(() => {
    let annule = false;

    (async () => {
      for (const m of medias) {
        if (urls.current[m.id]) continue;
        try {
          const blob = await apiService.getNubieAiMediaBlob(siteId, m.id);
          if (annule) return;
          const url = URL.createObjectURL(blob);
          urls.current[m.id] = url;
          setApercus((p) => ({ ...p, [m.id]: url }));
        } catch (err) {
          // Un apercu manquant n'empeche ni de placer ni de retirer la photo : on laisse
          // le cadre sans image plutot que de bloquer l'ecran.
          Logger.error('NubieAI: apercu de photo indisponible', err);
        }
      }

      for (const id of Object.keys(urls.current)) {
        if (!medias.some((m) => m.id === id)) {
          URL.revokeObjectURL(urls.current[id]);
          delete urls.current[id];
          setApercus((p) => {
            const n = { ...p };
            delete n[id];
            return n;
          });
        }
      }
    })();

    return () => {
      annule = true;
    };
  }, [medias, siteId]);

  // Au demontage seulement : tout revoquer.
  useEffect(
    () => () => {
      Object.values(urls.current).forEach((u) => URL.revokeObjectURL(u));
      urls.current = {};
    },
    []
  );

  return apercus;
}

/**
 * Remplace, dans l'apercu rendu par l'API, l'adresse de chaque photo par son URL d'objet.
 *
 * L'INJECTION EST FAITE PAR L'API, PAS ICI. `apercu_html` sort de la MEME fonction que
 * celle de la publication : la console ne fait que substituer des adresses qu'elle
 * detient deja. Refaire l'injection ici donnerait deux implementations de la meme chose,
 * qui divergeraient -- et c'est l'ecran qui perdrait.
 *
 * Une photo dont le blob n'est pas encore charge garde l'adresse de l'API, que l'iframe
 * ne peut pas atteindre : le cadre reste vide le temps du telechargement, ce qui est
 * exactement le comportement voulu.
 */
export function substituerApercus(
  html: string,
  medias: NubieAiMedia[],
  apercus: Record<string, string>
): string {
  let sortie = html;
  for (const m of medias) {
    const objet = apercus[m.id];
    if (m.url && objet) sortie = sortie.split(m.url).join(objet);
  }
  return sortie;
}
